/**
 * Consent Gate
 * Disables linked target elements until the consent checkbox is checked.
 * - Initializes only when at least one [data-consent-gate] root exists.
 * - Each root manages only its own selectors (from data-target-selectors).
 * - Handles async-injected targets (e.g. SC Back in Stock) via MutationObserver.
 * - Optional follow_targets: hide the gate until at least one target exists.
 */
(function () {
  'use strict';

  var GATED_CLASS = 'is-consent-gated';
  var OWNED_ATTRIBUTE = 'data-consent-gate-disabled';
  var OBSERVER_DEBOUNCE_MS = 100;

  /**
   * Wrap a callback so that bursts of calls collapse into a single trailing call.
   * @param {Function} callback
   * @param {number} wait
   * @returns {Function}
   */
  function debounce(callback, wait) {
    var timer = null;
    return function () {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        timer = null;
        callback();
      }, wait);
    };
  }

  /**
   * Parse a selector string separated by commas and/or newlines into an array.
   * @param {string} raw
   * @returns {string[]}
   */
  function parseSelectors(raw) {
    if (!raw) return [];
    return raw
      .split(/[\n,]+/)
      .map(function (selector) {
        return selector.trim();
      })
      .filter(function (selector) {
        return selector.length > 0;
      });
  }

  /**
   * Collect all elements matched by the given selectors.
   * Invalid selectors are ignored without throwing.
   * @param {string[]} selectors
   * @returns {Element[]}
   */
  function collectTargets(selectors) {
    var found = [];
    selectors.forEach(function (selector) {
      var nodes;
      try {
        nodes = document.querySelectorAll(selector);
      } catch (error) {
        return;
      }
      nodes.forEach(function (node) {
        if (found.indexOf(node) === -1) found.push(node);
      });
    });
    return found;
  }

  /**
   * Apply enabled/disabled state to a single target element.
   * Only the disabled state applied by this gate is released again: a target that
   * was already disabled by its owner (e.g. SC Back in Stock while submitting)
   * must stay disabled even after the checkbox is ticked.
   * @param {Element} element
   * @param {boolean} isEnabled
   */
  function applyState(element, isEnabled) {
    var supportsDisabled = 'disabled' in element;

    if (isEnabled) {
      element.classList.remove(GATED_CLASS);
      if (!element.hasAttribute(OWNED_ATTRIBUTE)) return;

      element.removeAttribute(OWNED_ATTRIBUTE);
      element.removeAttribute('aria-disabled');
      if (supportsDisabled) element.disabled = false;
      return;
    }

    element.classList.add(GATED_CLASS);
    element.setAttribute('aria-disabled', 'true');

    if (supportsDisabled && element.disabled && !element.hasAttribute(OWNED_ATTRIBUTE)) {
      // 已被外部禁用：保持现状，不接管其禁用状态
      return;
    }

    element.setAttribute(OWNED_ATTRIBUTE, '');
    if (supportsDisabled) element.disabled = true;
  }

  /**
   * Initialize a single consent-gate root.
   * @param {Element} root
   */
  function initRoot(root) {
    if (root.hasAttribute('data-consent-gate-ready')) return;
    root.setAttribute('data-consent-gate-ready', 'true');

    var checkbox = root.querySelector('[data-consent-gate-checkbox]');
    var selectors = parseSelectors(root.getAttribute('data-target-selectors'));
    var shouldFollowTargets = root.getAttribute('data-follow-targets') === 'true';
    if (!checkbox || selectors.length === 0) return;

    var sync = function () {
      var targets = collectTargets(selectors);

      if (shouldFollowTargets) {
        if (targets.length > 0) {
          root.removeAttribute('hidden');
        } else {
          root.setAttribute('hidden', '');
          return;
        }
      }

      var isEnabled = checkbox.checked;
      targets.forEach(function (element) {
        applyState(element, isEnabled);
      });
    };

    checkbox.addEventListener('change', sync);
    sync();

    var observer = new MutationObserver(
      debounce(function () {
        // root 已离开文档（section 被移除或 Theme Editor 重载）：停止观察
        if (!document.contains(root)) {
          observer.disconnect();
          return;
        }
        sync();
      }, OBSERVER_DEBOUNCE_MS)
    );
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Initialize all consent-gate roots on the page.
   */
  function init() {
    var roots = document.querySelectorAll('[data-consent-gate]');
    if (roots.length === 0) return;
    roots.forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
